import React, { useEffect, useState } from 'react';
import { connect } from 'dva';
import PropTypes from 'prop-types';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Point from '@arcgis/core/geometry/Point';
import SketchWidget from '@arcgis/core/widgets/Sketch';

import { FEATURE_TYPES, MAP_LAYER_IDS, MAP_TOOLS } from '../../../lib/constants';
import { getItemIndex } from '../../../lib/utils';
import actions from './actions';
import ghAnalytics from '../../../lib/analytics';
import SelectIcon from '../../../assets/svg/SelectIcon';
import styles from './SelectWidget.module.scss';

function SelectWidget(props) {
  const [createEventHandler, setCreateEventHandler] = useState(null);

  useEffect(() => {
    const params = {
      items: props.map.layers.items,
      key: 'id',
      reverse: true,
      value: MAP_LAYER_IDS.select
    }
    let selectLayerIndex = getItemIndex(params)
    if (selectLayerIndex > -1) {
      props.map.layers.remove(params.items[selectLayerIndex])
    } else {
      params.value = MAP_LAYER_IDS.gohunt
      selectLayerIndex = getItemIndex(params)
    }

    const newSelectLayer = new GraphicsLayer({
      id: MAP_LAYER_IDS.select,
      mode: 'on-the-ground',
    });

    const select = new SketchWidget({
      layer: newSelectLayer,
      label: 'select-widget',
      view: props.view,
      creationMode: 'continuous', // 'single',
      availableCreateTools: ['rectangle'],
      defaultCreateOptions: {
        hasZ: false,
      },
    });
    select.viewModel.updateOnGraphicClick = false;

    props.setSelectWidgetRef(select);

    let selectEventHandler = select.on('create', (e) => {
      if (e.state === 'complete') {
        const graphic = new Graphic({
          geometry: e.graphic.geometry,
          symbol: {
            type: 'polygon-3d', // autocasts as new PolygonSymbol3D()
            symbolLayers: [
              {
                type: 'fill', // autocasts as new FillSymbol3DLayer()
                material: {
                  color: [255, 255, 255, 0.2],
                },
                outline: {
                  color: '#fff',
                  size: 2,
                },
              },
            ],
          },
        });

        const layer = props.map.findLayerById(MAP_LAYER_IDS.select);
        layer.add(graphic);
        
        /*
        #NOTE: See the note at connect function as to why this was done.
        -- KD - June 26, 2020
        */
       if (props.visible.all || props.visible.features.length > 0) {
          props.getFeaturesList((featuresList) => {
            const selectedFeatures = []
            const visibleFeatures = []
            let feature = null
            for (let fli = 0; fli < featuresList.length; fli++) {
              feature = featuresList[fli]
              if (feature.isVisible) {
                visibleFeatures.push({
                  coordinates: feature.geometry.coordinates,
                  type: feature.properties.ghType,
                  uuid: feature.uuid
                })
              }
            }
            if (visibleFeatures.length > 0) {
              let point = null
              for (let vfi = 0; vfi < visibleFeatures.length; vfi++) {
                feature = visibleFeatures[vfi]
                switch (feature.type) {
                  case FEATURE_TYPES.Point:
                    point = new Point({
                      latitude: feature.coordinates[1],
                      longitude: feature.coordinates[0],
                    })
                    if (e.graphic.geometry.contains(point)) {
                      selectedFeatures.push(feature.uuid)
                    }
                    break
                  case FEATURE_TYPES.Polyline:
                    for (let primaryCoordinates of feature.coordinates) {
                      point = new Point({
                        latitude: primaryCoordinates[1],
                        longitude: primaryCoordinates[0],
                      })
                      if (e.graphic.geometry.contains(point)) {
                        selectedFeatures.push(feature.uuid)
                        break
                      }
                    }
                    break
                  case FEATURE_TYPES.Polygon:
                  case FEATURE_TYPES.Tracks:
                    nestedLoop: for (let primaryCoordinates of feature.coordinates) {
                      for (let secondaryCoordinates of primaryCoordinates) {
                        point = new Point({
                          latitude: secondaryCoordinates[1],
                          longitude: secondaryCoordinates[0],
                        })
                        if (e.graphic.geometry.contains(point)) {
                          selectedFeatures.push(feature.uuid)
                          break nestedLoop
                        }
                      }
                    }
                    break
                }
              }
            }
            if (selectedFeatures.length > 0) {
              ghAnalytics.logSelectFeatures(featuresList)
              props.addSelectedFeaturesFromMap(selectedFeatures)
            }
          })
        }
      } else if (e.state === 'cancel') {
        props.cancelWidget();
      }
    });

    setCreateEventHandler(selectEventHandler);
    props.map.add(newSelectLayer, selectLayerIndex)
    return () => {
      // const layer = props.map.findLayerById(MAP_LAYER_IDS.select);
      // props.map.remove(layer);
      if (createEventHandler) {
        createEventHandler.remove();
      }
    };
  }, [props.view]);

  if (!props.isMapReady) return null;

  return (
    <button
      aria-label='Select items'
      className={[
        'esri-widget--button',
        'esri-interactive',
        'esri-widget__button--selected',
        props.activeTool === MAP_TOOLS.Select ? styles.selected : '',
      ].join(' ')}
      title='Select items'
      onClick={
        props.activeTool === MAP_TOOLS.Select
          ? props.cancelWidget
          : props.beginSelectingFeatures
      }
    >
      <SelectIcon color='#ffffff' width={20} height={20} />
    </button>
  );
}

SelectWidget.propTypes = {
  activeTool: PropTypes.string,
  isMapReady: PropTypes.bool,
  isUpdatingLocation: PropTypes.bool,
  map: PropTypes.any,
  view: PropTypes.any,
  visible: PropTypes.object,

  // actions
  addSelectedFeaturesFromMap: PropTypes.func,
  beginSelectingFeatures: PropTypes.func,
  cancelWidget: PropTypes.func,
  getFeaturesList: PropTypes.func,
  setSelectWidgetRef: PropTypes.func,
};

export default connect((model) => {
  const { activeTool, isMapReady, isUpdatingLocation } = model.map
  const { preferences: { visible } } = model.features
  return {
    activeTool,
    isMapReady,
    isUpdatingLocation,
    visible
  };
}, actions)(SelectWidget);
